import {useCurrentScopeContext} from '@/context/useCurrentScopeContext'
import {useUsageContext} from '@/context/useUsageContext'
import {request} from '@/data/util/promiseRequest'
import {formatDate} from '@/utils/time'
import {useQuery} from '@tanstack/react-query'
import {endOfMonth} from 'date-fns'

type OrgSubscriptionResource = {
  subscription: {
    totalAllowedUsage: number | null
    unit: string
    name: string
  }
  granted: boolean
}

type OrgUsageSnapshot = {
  usage: {
    [key: string]: number
  }
}

export function useOrgMediaLibraryId() {
  const {org} = useCurrentScopeContext()
  return useQuery({
    queryKey: ['org-media-library-id', org?.id],
    queryFn: async () => {
      return request({
        headers: {'Content-Type': 'application/json'},
        url: `https://api.${process.env.host}/vX/media-libraries?organizationId=${org?.id}`,
        method: 'GET',
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.body.message)
        }
        return res.body as {data: {id: string}[]}
      })
    },
    select: (response) => response.data[0]?.id,
    enabled: !!org?.id,
  })
}

function useProductSubscriptionResource({
  productName,
  productId,
  resource,
}: {
  productName: string
  productId?: string
  resource: string
}) {
  return useQuery({
    queryKey: ['subscription-resource', productName, productId, resource],
    queryFn: async () => {
      return request({
        headers: {'Content-Type': 'application/json'},
        url: `https://api.${process.env.host}/vX/subscriptions/${productName}/${productId}/resources/${resource}`,
        method: 'GET',
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.body.message)
        }
        return res.body as OrgSubscriptionResource
      })
    },
    enabled: !!productId && !!productName && !!resource,
  })
}

export function useOrgSubscriptionResource({resource}: {resource: string}) {
  const {org} = useCurrentScopeContext()
  return useProductSubscriptionResource({
    productName: 'organization',
    productId: org?.id,
    resource,
  })
}

export function useMediaLibrarySubscriptionResource({resource}: {resource: string}) {
  const {data: mediaLibraryId} = useOrgMediaLibraryId()
  return useProductSubscriptionResource({
    productName: 'media-library',
    productId: mediaLibraryId,
    resource,
  })
}

function useResourceUsageSnapshot({
  resourceName,
  resourceId,
  compare = false,
}: {
  resourceName: string
  resourceId?: string
  compare?: boolean
}) {
  const {currentRange, currentPeriod, compareRange} = useUsageContext()

  const range = compare ? compareRange : currentRange
  const fromDate = range?.toDate ? endOfMonth(range?.toDate) : undefined
  const period = currentPeriod?.snapshot
  const formattedDate = fromDate ? formatDate(fromDate) : undefined
  const snapshotPeriod = period === 'year' ? 'previousPeriod' : 'startOfPeriod'

  return useQuery({
    queryKey: [
      'resource-usage-snapshot',
      resourceName,
      resourceId,
      period,
      formattedDate,
      snapshotPeriod,
    ],
    queryFn: async () => {
      return request({
        headers: {'Content-Type': 'application/json'},
        url: `https://api.${process.env.host}/vX/usage/${resourceName}/${resourceId}/snapshot`,
        method: 'GET',
        query: {period, date: formattedDate, snapshotPeriod},
      }).then((res) => {
        if (!res.ok) {
          throw new Error(res.body.message)
        }
        return res.body as OrgUsageSnapshot
      })
    },
    enabled: !!resourceId && !!resourceName,
  })
}

export function useOrgUsageSnapshot({compare} = {compare: false}) {
  const {org} = useCurrentScopeContext()
  return useResourceUsageSnapshot({resourceName: 'organization', resourceId: org?.id, compare})
}

export function useOrgMediaLibraryUsageSnapshot({compare} = {compare: false}) {
  const {data: orgMediaLibraryId} = useOrgMediaLibraryId()
  return useResourceUsageSnapshot({
    resourceName: 'media-library',
    resourceId: orgMediaLibraryId,
    compare,
  })
}
